import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Users, ClipboardList, Send, Printer, Trash2, CheckCircle, RotateCw } from 'lucide-react';

export const OBS_CRITERIA = [
  { id: 'adresse', label: "Validation de l'adresse et du numéro de rappel", hint: "Adresse exacte confirmée dès le début, numéro de rappel noté" },
  { id: 'sample', label: 'Questionnaire médical (SAMPLE)', hint: 'Symptômes, Allergies, Médicaments, Antécédents, dernier rePas, Événement déclencheur' },
  { id: 'red_flags', label: 'Détection des drapeaux rouges', hint: 'Signes de gravité reconnus (inconscience, douleur irradiante, dyspnée...)' },
  { id: 'priorisation', label: 'Priorisation et envoi des ressources', hint: 'Bonne priorité au bon moment, court-circuit si urgence vitale' },
  { id: 'consignes', label: "Consignes pré-arrivée et fin d'appel", hint: 'Position, ne rien manger, porte, documents, guide pour paramédics' },
  { id: 'ton', label: 'Ton, calme et empathie', hint: "Voix posée, sans jugement, garde le contrôle de l'appel" },
];

const RATING_CONFIG = {
  oui: { label: '✔ Réussi', color: '#16A34A', bg: '#F0FDF4' },
  partiel: { label: '~ Partiel', color: '#D97706', bg: '#FFFBEB' },
  non: { label: '✘ À travailler', color: '#DC2626', bg: '#FEF2F2' },
};

const ROTATION = [
  { manche: 1, roles: ['🎧 Répartiteur', '📋 Observateur', '📝 Scribe'] },
  { manche: 2, roles: ['📝 Scribe', '🎧 Répartiteur', '📋 Observateur'] },
  { manche: 3, roles: ['📋 Observateur', '📝 Scribe', '🎧 Répartiteur'] },
];

const TeamExercise = ({ studentInfo }) => {
  const myNumber = studentInfo?.student_number || '';
  const myName = studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : '';
  const [tab, setTab] = useState('team');
  const [team, setTeam] = useState(null);
  const [myObservations, setMyObservations] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  // Observation form state
  const [dispatcherName, setDispatcherName] = useState('');
  const [dispatcherNumber, setDispatcherNumber] = useState('');
  const [scenarioLabel, setScenarioLabel] = useState('');
  const [criteria, setCriteria] = useState({});
  const [globalComments, setGlobalComments] = useState('');
  const [saving, setSaving] = useState(false);

  // Submission form state
  const [subDispatcher, setSubDispatcher] = useState('');
  const [dispatcherSessions, setDispatcherSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [dispatcherObs, setDispatcherObs] = useState([]);
  const [selectedObsId, setSelectedObsId] = useState('');
  const [teamNote, setTeamNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [teamsResp, obsResp, subResp] = await Promise.all([
        axios.get(`${API}/teams`, { params: { student_number: myNumber } }),
        axios.get(`${API}/observations`, { params: { observer_number: myNumber } }),
        axios.get(`${API}/submissions`, { params: { student_number: myNumber } }),
      ]);
      setTeam(teamsResp.data[0] || null);
      setMyObservations(obsResp.data);
      setSubmissions(subResp.data);
    } catch (e) { console.error(e); }
  }, [myNumber]);

  useEffect(() => { if (myNumber) loadAll(); }, [myNumber, loadAll]);

  const setRating = (critId, rating) => {
    setCriteria(prev => ({ ...prev, [critId]: { ...prev[critId], rating: prev[critId]?.rating === rating ? null : rating } }));
  };
  const setCritComment = (critId, comment) => {
    setCriteria(prev => ({ ...prev, [critId]: { ...prev[critId], comment } }));
  };

  const saveObservation = async () => {
    if (!dispatcherName.trim()) { toast.error('Indiquez le nom du répartiteur observé'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/observations`, {
        team_id: team?.id || null,
        observer_number: myNumber,
        observer_name: myName,
        dispatcher_number: dispatcherNumber.trim(),
        dispatcher_name: dispatcherName.trim(),
        scenario_label: scenarioLabel.trim(),
        criteria,
        global_comments: globalComments.trim(),
      });
      toast.success('Grille d\'observation enregistrée');
      setCriteria({}); setGlobalComments(''); setScenarioLabel('');
      loadAll();
    } catch (e) { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const deleteObservation = async (id) => {
    if (!window.confirm('Supprimer cette grille?')) return;
    await axios.delete(`${API}/observations/${id}`).catch(() => {});
    loadAll();
  };

  const printGrid = (obs = null) => {
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const crit = obs ? obs.criteria : criteria;
    const rows = OBS_CRITERIA.map(c => {
      const r = crit[c.id] || {};
      const cfg = RATING_CONFIG[r.rating];
      return `<tr>
        <td style="padding:8px;border:1px solid #CBD5E1;"><strong>${c.label}</strong><br/><span style="font-size:11px;color:#64748B;">${c.hint}</span></td>
        <td style="padding:8px;border:1px solid #CBD5E1;text-align:center;font-weight:bold;color:${cfg ? cfg.color : '#94A3B8'};">${cfg ? cfg.label : '—'}</td>
        <td style="padding:8px;border:1px solid #CBD5E1;font-size:12px;">${(r.comment || '').replace(/</g, '&lt;')}</td>
      </tr>`;
    }).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Grille d'observation</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#1E293B;}table{width:100%;border-collapse:collapse;font-size:13px;}
      @media print{.no-print{display:none;}}</style></head><body>
      <h2 style="color:#002768;">📋 Grille d'observation — Simulation 911</h2>
      <p style="font-size:13px;"><strong>Répartiteur observé :</strong> ${esc((obs ? obs.dispatcher_name : dispatcherName)) || '________________'} &nbsp;&nbsp;
      <strong>Observateur :</strong> ${esc((obs ? obs.observer_name : myName)) || '________________'}<br/>
      <strong>Scénario :</strong> ${esc((obs ? obs.scenario_label : scenarioLabel)) || '________________'} &nbsp;&nbsp;
      <strong>Date :</strong> ${new Date(obs ? obs.created_at : Date.now()).toLocaleDateString('fr-CA')}</p>
      <table><thead><tr style="background:#002768;color:white;">
      <th style="padding:8px;border:1px solid #CBD5E1;text-align:left;">Critère</th>
      <th style="padding:8px;border:1px solid #CBD5E1;width:110px;">Évaluation</th>
      <th style="padding:8px;border:1px solid #CBD5E1;text-align:left;width:35%;">Commentaires</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <p style="margin-top:16px;font-size:13px;"><strong>Commentaires généraux :</strong><br/>${esc((obs ? obs.global_comments : globalComments) || '') || '_______________________________________________'}</p>
      <p style="font-size:11px;color:#94A3B8;margin-top:24px;">Simulateur 911 — Collège Montmorency</p>
      <button class="no-print" onclick="window.print()" style="margin-top:12px;padding:8px 16px;">Imprimer</button>
      </body></html>`);
    w.document.close();
  };

  const loadDispatcherSessions = async (member) => {
    setSubDispatcher(member.student_number);
    setSelectedSessionId(''); setSelectedObsId('');
    try {
      const [healthResp, simResp, obsResp] = await Promise.all([
        axios.get(`${API}/health/sessions`, { params: { student_number: member.student_number } }),
        axios.get(`${API}/sessions`, { params: { student_number: member.student_number } }),
        axios.get(`${API}/observations`, { params: { dispatcher_number: member.student_number } }),
      ]);
      const health = (healthResp.data || []).filter(s => s.status === 'completed').map(s => ({
        id: s.id, type: 'health',
        label: `Santé — ${s.protocol_id} (${s.mode === 'guided' ? 'guidé' : 'simulation'})${s.final_priority ? ` — P${s.final_priority}` : ''}`,
        date: s.completed_at || s.started_at,
      }));
      const sims = (simResp.data || []).map(s => ({
        id: s.id, type: 'simulateur',
        label: `Appel 911 — ${s.scenario_title || s.scenario_id || 'scénario'}`,
        date: s.start_time,
      }));
      setDispatcherSessions([...health, ...sims].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 25));
      setDispatcherObs(obsResp.data || []);
    } catch (e) { toast.error('Erreur de chargement des sessions'); }
  };

  const submitForCorrection = async () => {
    const sess = dispatcherSessions.find(s => s.id === selectedSessionId);
    if (!sess) { toast.error('Choisissez une session'); return; }
    const member = (team?.members || []).find(m => m.student_number === subDispatcher);
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        team_id: team?.id || null,
        team_name: team?.name || '',
        session_id: sess.id,
        session_type: sess.type,
        session_label: sess.label,
        dispatcher_number: subDispatcher,
        dispatcher_name: member?.name || subDispatcher,
        observation_id: selectedObsId || null,
        submitted_by: myNumber,
        team_note: teamNote.trim(),
      });
      toast.success('Session soumise pour correction! 🎉');
      setSelectedSessionId(''); setSelectedObsId(''); setTeamNote('');
      loadAll();
    } catch (e) { toast.error('Erreur lors de la soumission'); }
    finally { setSubmitting(false); }
  };

  const tabs = [
    { id: 'team', icon: Users, label: 'Mon équipe' },
    { id: 'grid', icon: ClipboardList, label: 'Grille d\'observation' },
    { id: 'submit', icon: Send, label: 'Soumission & corrections' },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="team-exercise-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold" style={{ color: '#002768' }}>Exercice d'équipe</h1>
          <p className="text-gray-500 text-sm mt-1">Pratiquez en équipe de 2-3 : répartiteur, observateur, scribe — puis soumettez votre meilleure simulation</p>
        </div>

        <div className="flex justify-center gap-2 mb-6 flex-wrap">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === t.id ? 'text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50 border'}`}
                style={tab === t.id ? { backgroundColor: '#002768' } : {}}
                data-testid={`team-tab-${t.id}`}>
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>

        {/* ---- TAB: MON ÉQUIPE ---- */}
        {tab === 'team' && (
          <div className="space-y-4" data-testid="team-tab-content">
            {team ? (
              <div className="bg-white rounded-2xl border p-6 shadow-sm">
                <h2 className="text-lg font-bold mb-1" style={{ color: '#002768' }}>👥 {team.name}</h2>
                <div className="flex flex-wrap gap-2 mb-5">
                  {team.members.map(m => (
                    <span key={m.student_number} className={`px-3 py-1.5 rounded-full text-sm font-medium ${m.student_number === myNumber ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-300' : 'bg-gray-100 text-gray-700'}`}>
                      {m.name} {m.student_number === myNumber && '(moi)'}
                    </span>
                  ))}
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5"><RotateCw className="w-4 h-4" /> Rotation suggérée des rôles</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase text-gray-500 border-b">
                      <th className="py-2 pr-3">Manche</th>
                      {team.members.slice(0, 3).map(m => <th key={m.student_number} className="py-2 pr-3">{m.name.split(' ')[0]}</th>)}
                    </tr></thead>
                    <tbody>
                      {ROTATION.slice(0, team.members.length === 2 ? 2 : 3).map(r => (
                        <tr key={r.manche} className="border-b border-gray-100">
                          <td className="py-2 pr-3 font-bold text-gray-700">Manche {r.manche}</td>
                          {team.members.slice(0, 3).map((m, i) => (
                            <td key={m.student_number} className="py-2 pr-3">{team.members.length === 2 && r.roles[i] === '📝 Scribe' ? '📋 Observateur' : r.roles[i]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="no-team-message">
                <p className="text-amber-800 font-medium">Vous n'êtes pas encore dans une équipe.</p>
                <p className="text-amber-600 text-sm mt-1">Votre instructeur crée les équipes dans le panneau Admin. Vous pouvez quand même utiliser la grille d'observation!</p>
              </div>
            )}
            <div className="bg-white rounded-2xl border p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-700 mb-3">📖 Déroulement de l'exercice</h3>
              <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                <li><strong>Répartiteur</strong> : lance une simulation (Santé → Mode Simulation, scénario indiqué par l'instructeur) et partage son écran sur Teams.</li>
                <li><strong>Observateur</strong> : ouvre la <em>Grille d'observation</em> ici sur son propre écran et coche en direct.</li>
                <li><strong>Scribe</strong> (équipe de 3) : note les informations paramédics recueillies (âge, médicaments, allergies).</li>
                <li>On <strong>tourne les rôles</strong> et on recommence avec un nouveau scénario.</li>
                <li>À la fin : l'équipe choisit sa <strong>meilleure session</strong> et la soumet pour correction (onglet Soumission).</li>
              </ol>
            </div>
          </div>
        )}

        {/* ---- TAB: GRILLE ---- */}
        {tab === 'grid' && (
          <div className="space-y-4" data-testid="grid-tab-content">
            <div className="bg-white rounded-2xl border p-6 shadow-sm">
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Répartiteur observé *</label>
                  {team ? (
                    <select value={dispatcherNumber} onChange={e => {
                      const m = team.members.find(x => x.student_number === e.target.value);
                      setDispatcherNumber(e.target.value); setDispatcherName(m?.name || '');
                    }} className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" data-testid="obs-dispatcher-select">
                      <option value="">— Choisir —</option>
                      {team.members.filter(m => m.student_number !== myNumber).map(m => (
                        <option key={m.student_number} value={m.student_number}>{m.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input value={dispatcherName} onChange={e => setDispatcherName(e.target.value)} placeholder="Nom du coéquipier"
                      className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" data-testid="obs-dispatcher-input" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-500">Scénario / déterminant</label>
                  <input value={scenarioLabel} onChange={e => setScenarioLabel(e.target.value)} placeholder="Ex : Douleur thoracique — l'appelant est le patient"
                    className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" data-testid="obs-scenario-input" />
                </div>
              </div>

              <div className="space-y-3">
                {OBS_CRITERIA.map(c => {
                  const cur = criteria[c.id] || {};
                  return (
                    <div key={c.id} className="p-3 rounded-xl border" style={{ backgroundColor: cur.rating ? RATING_CONFIG[cur.rating].bg : '#FAFAFA' }} data-testid={`criterion-${c.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-sm font-bold text-gray-800">{c.label}</p>
                          <p className="text-xs text-gray-400">{c.hint}</p>
                        </div>
                        <div className="flex gap-1">
                          {Object.entries(RATING_CONFIG).map(([key, cfg]) => (
                            <button key={key} onClick={() => setRating(c.id, key)}
                              className="px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all"
                              style={cur.rating === key ? { backgroundColor: cfg.color, color: 'white', borderColor: cfg.color } : { color: cfg.color, borderColor: '#E5E7EB', backgroundColor: 'white' }}
                              data-testid={`rate-${c.id}-${key}`}>
                              {cfg.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <input value={cur.comment || ''} onChange={e => setCritComment(c.id, e.target.value)} placeholder="Commentaire (facultatif)..."
                        className="w-full mt-2 px-3 py-1.5 rounded-lg border text-xs" data-testid={`comment-${c.id}`} />
                    </div>
                  );
                })}
              </div>

              <textarea value={globalComments} onChange={e => setGlobalComments(e.target.value)} rows={2}
                placeholder="Commentaires généraux : points forts, à améliorer..."
                className="w-full mt-4 px-3 py-2 rounded-lg border text-sm" data-testid="obs-global-comments" />

              <div className="flex gap-2 mt-4">
                <button onClick={saveObservation} disabled={saving}
                  className="flex-1 py-2.5 rounded-lg text-white font-bold text-sm hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#002768' }} data-testid="save-observation-btn">
                  {saving ? 'Enregistrement...' : '💾 Enregistrer la grille'}
                </button>
                <button onClick={() => printGrid(null)} className="px-4 py-2.5 rounded-lg border font-bold text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5" data-testid="print-blank-grid-btn">
                  <Printer className="w-4 h-4" /> Imprimer
                </button>
              </div>
            </div>

            {myObservations.length > 0 && (
              <div className="bg-white rounded-2xl border p-6 shadow-sm">
                <h3 className="text-sm font-bold text-gray-700 mb-3">Mes grilles enregistrées ({myObservations.length})</h3>
                <div className="space-y-2">
                  {myObservations.map(o => (
                    <div key={o.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border" data-testid={`obs-row-${o.id}`}>
                      <div className="text-sm">
                        <span className="font-semibold text-gray-800">{o.dispatcher_name}</span>
                        <span className="text-gray-400"> — {o.scenario_label || 'scénario non précisé'} — {new Date(o.created_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => printGrid(o)} className="p-1.5 rounded hover:bg-gray-200" title="Imprimer"><Printer className="w-4 h-4 text-gray-500" /></button>
                        <button onClick={() => deleteObservation(o.id)} className="p-1.5 rounded hover:bg-red-100" title="Supprimer" data-testid={`delete-obs-${o.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---- TAB: SOUMISSION ---- */}
        {tab === 'submit' && (
          <div className="space-y-4" data-testid="submit-tab-content">
            <div className="bg-white rounded-2xl border p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-700 mb-3">🏆 Soumettre votre meilleure simulation pour correction</h3>
              {!team ? (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">Vous devez faire partie d'une équipe pour soumettre. Demandez à votre instructeur.</p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500">1. Qui était le répartiteur de la session choisie?</label>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {team.members.map(m => (
                        <button key={m.student_number} onClick={() => loadDispatcherSessions(m)}
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${subDispatcher === m.student_number ? 'text-white' : 'text-gray-600 bg-white hover:bg-gray-50'}`}
                          style={subDispatcher === m.student_number ? { backgroundColor: '#002768', borderColor: '#002768' } : {}}
                          data-testid={`sub-dispatcher-${m.student_number}`}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  {subDispatcher && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500">2. Choisir la session (les plus récentes)</label>
                      <select value={selectedSessionId} onChange={e => setSelectedSessionId(e.target.value)}
                        className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" data-testid="sub-session-select">
                        <option value="">— Choisir une session complétée —</option>
                        {dispatcherSessions.map(s => (
                          <option key={s.id} value={s.id}>{s.label} — {s.date ? new Date(s.date).toLocaleString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</option>
                        ))}
                      </select>
                      {dispatcherSessions.length === 0 && <p className="text-xs text-gray-400 mt-1">Aucune session complétée trouvée pour ce membre.</p>}
                    </div>
                  )}
                  {subDispatcher && dispatcherObs.length > 0 && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500">3. Joindre une grille d'observation (facultatif)</label>
                      <select value={selectedObsId} onChange={e => setSelectedObsId(e.target.value)}
                        className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" data-testid="sub-obs-select">
                        <option value="">— Aucune —</option>
                        {dispatcherObs.map(o => (
                          <option key={o.id} value={o.id}>Par {o.observer_name} — {o.scenario_label || 'scénario non précisé'} ({new Date(o.created_at).toLocaleDateString('fr-CA')})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <textarea value={teamNote} onChange={e => setTeamNote(e.target.value)} rows={2}
                    placeholder="Message à l'instructeur (facultatif) : pourquoi cette session, ce qu'on a trouvé difficile..."
                    className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="sub-team-note" />
                  <button onClick={submitForCorrection} disabled={submitting || !selectedSessionId}
                    className="w-full py-2.5 rounded-lg text-white font-bold text-sm hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#16A34A' }} data-testid="submit-for-correction-btn">
                    {submitting ? 'Envoi...' : '📤 Soumettre pour correction'}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border p-6 shadow-sm">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Nos soumissions ({submissions.length})</h3>
              {submissions.length === 0 ? (
                <p className="text-sm text-gray-400">Aucune soumission pour l'instant.</p>
              ) : (
                <div className="space-y-2">
                  {submissions.map(s => (
                    <div key={s.id} className="p-3 rounded-xl border bg-gray-50" data-testid={`submission-row-${s.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm">
                          <span className="font-semibold text-gray-800">{s.session_label}</span>
                          <span className="text-gray-400"> — répartiteur : {s.dispatcher_name}</span>
                        </div>
                        {s.status === 'corrected' ? (
                          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-testid={`sub-grade-${s.id}`}>
                            <CheckCircle className="w-3.5 h-3.5" /> Corrigé {s.grade != null ? `— ${s.grade}/100` : ''}
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">En attente de correction</span>
                        )}
                      </div>
                      {s.status === 'corrected' && s.instructor_comments && (
                        <p className="mt-2 text-xs text-gray-600 bg-white rounded-lg p-2 border"><strong>Commentaires de l'instructeur :</strong> {s.instructor_comments}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamExercise;
